import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { brl } from "../lib/format";
import { Plus, Trash, PencilSimple, FloppyDisk, X, FilePdf, FileXls, MagnifyingGlass, Package, ArrowUp, ArrowDown, UploadSimple, DownloadSimple } from "@phosphor-icons/react";
import PhotoAIField from "../components/PhotoAIField";
import LazyImage from "../components/LazyImage";
import OrphanProductsPanel from "../components/OrphanProductsPanel";
import { toast } from "sonner";
import MoneyInput, { parseMoney } from "../components/MoneyInput";
import ContractorProducts from "../components/ContractorProducts";
import { useAuth } from "../context/AuthContext";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const EMPTY = { code: "", description: "", department_id: "", contractor_id: "", price: "", qty: "", brand: "", size: "", model: "", color: "", photo_url: "" };

export default function Products() {
  const { user } = useAuth();
  if (user?.contractor_id) return <ContractorProducts />;
  return <ProductsAdmin />;
}

function ProductsAdmin() {
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all"); // all | store | <contractor_id>
  const [status, setStatus] = useState("all"); // all | sold | available
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [search, setSearch] = useState("");
  const [stockFor, setStockFor] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [saving, setSaving] = useState(false);

  const buildParams = useCallback(() => {
    const p = {};
    if (filter === "store") p.contractor_id = "none";
    else if (filter !== "all") p.contractor_id = filter;
    if (departmentFilter) p.department_id = departmentFilter;
    if (status !== "all") p.status = status;
    if (search.trim()) p.search = search.trim();
    return p;
  }, [filter, departmentFilter, status, search]);

  const load = useCallback(async () => {
    const [p, d, c] = await Promise.all([
      api.get("/products", { params: buildParams() }),
      api.get("/departments"),
      api.get("/contractors"),
    ]);
    setItems(p.data);
    setDepartments(d.data);
    setContractors(c.data);
  }, [buildParams]);
  useEffect(() => { load(); }, [load]);

  const downloadReport = async (kind /* pdf | xlsx */) => {
    try {
      const resp = await api.get(`/products/report.${kind}`, {
        params: { ...buildParams(), _: Date.now() },
        responseType: "blob",
        headers: { "Cache-Control": "no-cache" },
      });
      const blob = new Blob([resp.data], {
        type: kind === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `produtos.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Relatório ${kind.toUpperCase()} baixado`);
    } catch { toast.error(`Falha ao gerar ${kind.toUpperCase()}`); }
  };

  const exportCsv = async () => {
    try {
      const resp = await api.get("/products/export.csv", {
        params: { ...buildParams(), _: Date.now() },
        responseType: "blob",
        headers: { "Cache-Control": "no-cache" },
      });
      const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "produtos.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup CSV baixado");
    } catch { toast.error("Falha ao exportar CSV"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.code.trim() || !form.description.trim()) return toast.error("Preencha código e descrição");
    const payload = {
      code: form.code.trim(),
      description: form.description.trim(),
      department_id: form.department_id || null,
      contractor_id: form.contractor_id || null,
      price: parseMoney(form.price),
      brand: form.brand?.trim() || null,
      size: form.size?.trim() || null,
      model: form.model?.trim() || null,
      color: form.color?.trim() || null,
      photo_url: form.photo_url || null,
    };
    const qty = parseInt(String(form.qty).replace(/[^\d-]/g, ""), 10);
    setSaving(true);
    try {
      let productId = editing;
      if (editing) {
        await api.put(`/products/${editing}`, payload);
      } else {
        const { data } = await api.post("/products", payload);
        productId = data.id;
      }
      if (qty && qty > 0 && productId) {
        await api.post(`/products/${productId}/stock`, { kind: "in", quantity: qty, note: "Entrada via cadastro" });
        toast.success(`${editing ? "Produto atualizado" : "Produto cadastrado"} · +${qty} em estoque`);
      } else {
        toast.success(editing ? "Produto atualizado" : "Produto cadastrado");
      }
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este produto?")) return;
    await api.delete(`/products/${id}`);
    load();
  };

  const startEdit = async (p) => {
    setEditing(p.id);
    // Fetch the FULL product (includes base64 photo_url) — list payload is
    // slim and doesn't carry photos. Pre-fill with light fields immediately so
    // the form opens instantly, then patch in the photo when it arrives.
    setForm({
      code: p.code,
      description: p.description,
      department_id: p.department_id || "",
      contractor_id: p.contractor_id || "",
      price: String(p.price).replace(".", ","),
      qty: "",
      brand: p.brand || "",
      size: p.size || "",
      model: p.model || "",
      color: p.color || "",
      photo_url: "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
    try {
      const { data } = await api.get(`/products/${p.id}`);
      setForm((f) => f && editing === p.id ? { ...f, photo_url: data.photo_url || "" } : { ...f, photo_url: data.photo_url || "" });
    } catch {
      // non-blocking — user can still edit text fields; saving without a new
      // photo_url will null the picture, but that's handled below.
    }
  };

  const cancelEdit = () => { setEditing(null); setForm(EMPTY); };

  const editingProduct = editing ? items.find(p => p.id === editing) : null;

  const depColor = (id) => departments.find(d => d.id === id)?.color || "#FFFDDE";

  const totalRevenue = items.reduce((s, p) => s + (p.revenue || 0), 0);
  const soldCount = items.filter(p => p.is_sold).length;

  return (
    <div data-testid="products-page" className="space-y-8">
      <header>
        <p className="label-brutal">Catálogo</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black">Produtos</h1>
        <p className="text-brand-ink2 mt-2">Cadastre uma vez e reutilize durante a live. Vincule ao contratante quando for peça de terceiro.</p>
      </header>

      <form onSubmit={submit} className="card-brutal p-6 space-y-4">
        <PhotoAIField
          photoUrl={form.photo_url}
          onPhoto={(dataUrl) => setForm(f => ({ ...f, photo_url: dataUrl }))}
          onExtract={(fields) => setForm(f => ({
            ...f,
            description: fields.description || f.description,
            brand: fields.brand || f.brand,
            size: fields.size || f.size,
            model: fields.model || f.model,
            color: fields.color || f.color,
            code: f.code || fields.suggested_code || "",
            price: f.price || (fields.estimated_price != null ? String(fields.estimated_price).replace(".", ",") : ""),
          }))}
        />

        <div className="grid grid-cols-1 md:grid-cols-[110px_1.3fr_1fr_1fr_120px_110px_auto] gap-4 items-end">
        <div>
          <label className="label-brutal">Código</label>
          <input data-testid="product-code-input" className="input-brutal" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="001" />
        </div>
        <div>
          <label className="label-brutal">Descrição</label>
          <input data-testid="product-description-input" className="input-brutal" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ex: Blusa cropped rosa" />
        </div>
        <div>
          <label className="label-brutal">Departamento</label>
          <select data-testid="product-department-select" className="input-brutal" value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}>
            <option value="">—</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-brutal">Contratante</label>
          <select data-testid="product-contractor-select" className="input-brutal" value={form.contractor_id} onChange={(e) => setForm({ ...form, contractor_id: e.target.value })}>
            <option value="">Nossa loja</option>
            {contractors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-brutal">Preço (R$)</label>
          <MoneyInput data-testid="product-price-input" value={form.price} onChange={(v) => setForm({ ...form, price: v })} />
        </div>
        <div>
          <label className="label-brutal">
            Quantidade
            {editingProduct && (
              <span className="ml-1 text-brand-ink2 normal-case font-normal">
                · saldo: <strong>{editingProduct.stock_balance}</strong>
              </span>
            )}
          </label>
          <input
            data-testid="product-qty-input"
            type="number"
            min="0"
            inputMode="numeric"
            className="input-brutal"
            placeholder={editing ? "+ entrada" : "Ex: 10"}
            value={form.qty}
            onChange={(e) => setForm({ ...form, qty: e.target.value })}
          />
        </div>
        <div className="flex gap-2">
          <button data-testid="save-product-btn" type="submit" disabled={saving} className="btn-pink h-[52px] disabled:opacity-60">
            {editing ? <><FloppyDisk weight="bold" size={18} />{saving ? "Salvando…" : "Salvar"}</> : <><Plus weight="bold" size={18} />{saving ? "Salvando…" : "Adicionar"}</>}
          </button>
          {editing && (
            <button data-testid="cancel-edit-btn" type="button" onClick={cancelEdit} className="btn-white h-[52px]">
              <X weight="bold" size={18} />
            </button>
          )}
        </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="label-brutal">Marca</label>
            <input data-testid="product-brand-input" className="input-brutal" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Ex: Zara" />
          </div>
          <div>
            <label className="label-brutal">Tamanho</label>
            <input data-testid="product-size-input" className="input-brutal" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder="Ex: P / 38" />
          </div>
          <div>
            <label className="label-brutal">Modelo</label>
            <input data-testid="product-model-input" className="input-brutal" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="Ex: cropped, tubinho" />
          </div>
          <div>
            <label className="label-brutal">Cor</label>
            <input data-testid="product-color-input" className="input-brutal" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="Ex: rosa claro" />
          </div>
        </div>
      </form>

      <OrphanProductsPanel onReload={load} />

      <div className="card-brutal p-4 bg-brand-cream space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="label-brutal !mb-0">Contratante:</span>
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">Todos</FilterChip>
          <FilterChip active={filter === "store"} onClick={() => setFilter("store")} testid="filter-store">Nossa loja</FilterChip>
          {contractors.map(c => (
            <FilterChip key={c.id} active={filter === c.id} onClick={() => setFilter(c.id)} testid={`filter-ctr-${c.id}`}>
              {c.name}
            </FilterChip>
          ))}
        </div>        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1.5fr_auto_auto_auto_auto] gap-3 items-end">
          <div>
            <label className="label-brutal">Situação</label>
            <select data-testid="filter-status" className="input-brutal" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="all">Todos</option>
              <option value="sold">Vendidos</option>
              <option value="available">Disponíveis</option>
            </select>
          </div>
          <div>
            <label className="label-brutal">Departamento</label>
            <select data-testid="filter-department" className="input-brutal" value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
              <option value="">Todos</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-brutal">Buscar</label>
            <div className="relative">
              <MagnifyingGlass weight="bold" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-ink2" />
              <input data-testid="filter-search" className="input-brutal !pl-9" placeholder="código ou descrição" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
          <button data-testid="download-pdf" onClick={() => downloadReport("pdf")} className="btn-pink h-[52px]">
            <FilePdf weight="bold" size={16} /> PDF
          </button>
          <button data-testid="download-xlsx" onClick={() => downloadReport("xlsx")} className="btn-white h-[52px]">
            <FileXls weight="bold" size={16} /> Excel
          </button>
          <button data-testid="export-csv" onClick={exportCsv} className="btn-white h-[52px]">
            <DownloadSimple weight="bold" size={16} /> CSV
          </button>
          <button data-testid="open-import" onClick={() => setShowImport(true)} className="btn-white h-[52px]">
            <UploadSimple weight="bold" size={16} /> Importar CSV
          </button>
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          <span className="badge-brutal !bg-white"><strong>{items.length}</strong> produto(s)</span>
          <span className="badge-brutal !bg-brand-success !text-white"><strong>{soldCount}</strong> vendido(s)</span>
          <span className="badge-brutal !bg-brand-yellow"><strong>{items.length - soldCount}</strong> disponível(is)</span>
          <span className="badge-brutal !bg-brand-mint">Receita: <strong>{brl(totalRevenue)}</strong></span>
        </div>
      </div>

      <div className="card-brutal overflow-x-auto">
        <table className="tbl w-full">
          <thead>
            <tr>
              <th className="w-14">Foto</th>
              <th>Código</th>
              <th>Descrição</th>
              <th>Depto</th>
              <th>Contratante</th>
              <th className="text-right">Preço</th>
              <th className="w-24">Situação</th>
              <th className="w-20 text-right">Saldo</th>
              <th className="w-20 text-right">Qtd vend.</th>
              <th className="w-24 text-right">Preço médio</th>
              <th className="w-24 text-right">Receita</th>
              <th className="w-32 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={12} className="text-center text-brand-ink2 py-8">Nenhum produto encontrado.</td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} data-testid={`product-row-${p.id}`}>
                <td>
                  {p.has_photo ? (
                    <LazyImage
                      src={`${API_BASE}/api/products/${p.id}/photo?w=96`}
                      alt=""
                      imgTestid={`product-thumb-${p.id}`}
                      className="w-10 h-10 border-2 border-brand-ink"
                    />
                  ) : <div className="w-10 h-10 border-2 border-brand-ink bg-brand-cream" />}
                </td>
                <td className="font-bold">{p.code}</td>
                <td>
                  <div>{p.description}</div>
                  {(p.brand || p.size || p.color) && (
                    <div className="text-[11px] text-brand-ink2 mt-0.5">
                      {[p.brand, p.model, p.color, p.size ? `T:${p.size}` : null].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </td>
                <td>
                  {p.department_name ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="w-3 h-3 border-2 border-brand-ink" style={{ background: depColor(p.department_id) }} />
                      <span>{p.department_name}</span>
                    </span>
                  ) : <span className="text-brand-ink2">—</span>}
                </td>
                <td>
                  {p.contractor_name
                    ? <span className="badge-brutal !bg-brand-yellow">{p.contractor_name}</span>
                    : <span className="text-brand-ink2 text-sm">Nossa loja</span>}
                </td>
                <td className="text-right font-bold">
                  <PriceCell product={p} onSaved={load} />
                </td>
                <td>
                  {p.is_sold
                    ? <span data-testid={`status-sold-${p.id}`} className="badge-brutal !bg-brand-success !text-white">Vendido</span>
                    : <span data-testid={`status-avail-${p.id}`} className="badge-brutal !bg-brand-yellow">Disponível</span>}
                </td>
                <td className="text-right">
                  <StockCell product={p} onSaved={load} />
                </td>
                <td className="text-right">{p.sold_qty || 0}</td>
                <td className="text-right">{p.avg_sale_price ? brl(p.avg_sale_price) : "—"}</td>
                <td className="text-right font-bold">{p.revenue ? brl(p.revenue) : "—"}</td>
                <td className="text-right">
                  <div className="inline-flex gap-1">
                    <button data-testid={`stock-btn-${p.id}`} onClick={() => setStockFor(p)} title="Movimentar estoque" className="p-2 border-2 border-brand-ink bg-white hover:bg-brand-mint">
                      <Package weight="bold" size={14} />
                    </button>
                    <button data-testid={`edit-product-${p.id}`} onClick={() => startEdit(p)} className="p-2 border-2 border-brand-ink bg-white hover:bg-brand-yellow">
                      <PencilSimple weight="bold" size={14} />
                    </button>
                    <button data-testid={`delete-product-${p.id}`} onClick={() => remove(p.id)} className="p-2 border-2 border-brand-ink bg-white hover:bg-brand-peach">
                      <Trash weight="bold" size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {stockFor && (
        <StockModal
          product={stockFor}
          onClose={() => setStockFor(null)}
          onChanged={() => { setStockFor(null); load(); }}
        />
      )}

      {showImport && (
        <ImportCsvModal
          onClose={() => setShowImport(false)}
          onImported={() => { setShowImport(false); load(); }}
        />
      )}
    </div>
  );
}

function StockModal({ product, onClose, onChanged }) {
  const [movs, setMovs] = useState([]);
  const [kind, setKind] = useState("in");
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/products/${product.id}/stock`);
      setMovs(data);
    } catch { toast.error("Erro ao carregar histórico"); }
  }, [product.id]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const q = parseInt(qty, 10);
    if (!q || q <= 0) return toast.error("Informe uma quantidade positiva");
    setSaving(true);
    try {
      await api.post(`/products/${product.id}/stock`, { kind, quantity: q, note: note.trim() || null });
      toast.success(`${kind === "in" ? "Entrada" : "Saída"} registrada`);
      setQty(""); setNote("");
      await load();
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    } finally { setSaving(false); }
  };

  const remove = async (mid) => {
    if (!window.confirm("Excluir este movimento?")) return;
    try {
      await api.delete(`/products/${product.id}/stock/${mid}`);
      toast.success("Removido");
      await load();
      onChanged();
    } catch { toast.error("Erro"); }
  };

  const balance = movs.reduce((s, m) => s + (m.kind === "in" ? m.quantity : -m.quantity), 0);

  return (
    <div className="fixed inset-0 bg-brand-ink/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div data-testid="stock-modal" className="card-brutal w-full max-w-2xl bg-white p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between mb-4">
          <div>
            <p className="label-brutal">Estoque</p>
            <h2 className="font-heading text-2xl font-black">{product.code} · {product.description}</h2>
            <p className="mt-1">
              <span className="badge-brutal !bg-brand-mint">Saldo: {balance}</span>
            </p>
          </div>
          <button onClick={onClose} className="p-2 border-2 border-brand-ink"><X weight="bold" size={16} /></button>
        </div>
        <form onSubmit={submit} className="card-brutal p-4 bg-brand-cream grid grid-cols-1 md:grid-cols-[1fr_1fr_2fr_auto] gap-3 items-end mb-4">
          <div>
            <label className="label-brutal">Tipo</label>
            <select data-testid="stock-kind" className="input-brutal" value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="in">Entrada</option>
              <option value="out">Saída</option>
            </select>
          </div>
          <div>
            <label className="label-brutal">Quantidade</label>
            <input data-testid="stock-qty" className="input-brutal" type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} />
          </div>
          <div>
            <label className="label-brutal">Observação</label>
            <input data-testid="stock-note" className="input-brutal" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex.: reposição contratante" />
          </div>
          <button data-testid="stock-submit" type="submit" disabled={saving} className="btn-pink h-[52px]">
            <Plus weight="bold" size={16} /> Registrar
          </button>
        </form>
        <div className="card-brutal overflow-x-auto">
          <table className="tbl w-full">
            <thead>
              <tr><th className="w-32">Data</th><th className="w-24">Tipo</th><th className="w-20 text-right">Qtd</th><th>Obs.</th><th className="w-16"></th></tr>
            </thead>
            <tbody>
              {movs.length === 0 && <tr><td colSpan={5} className="text-center text-brand-ink2 py-6">Nenhum movimento.</td></tr>}
              {movs.map(m => (
                <tr key={m.id} data-testid={`mov-${m.id}`}>
                  <td className="text-sm">{new Date(m.created_at).toLocaleString("pt-BR")}</td>
                  <td>
                    {m.kind === "in"
                      ? <span className="badge-brutal !bg-brand-success !text-white"><ArrowUp weight="bold" size={12} /> Entrada</span>
                      : <span className="badge-brutal !bg-brand-peach"><ArrowDown weight="bold" size={12} /> Saída</span>}
                  </td>
                  <td className="text-right font-bold">{m.quantity}</td>
                  <td className="text-sm">{m.note || "—"}</td>
                  <td className="text-right">
                    <button onClick={() => remove(m.id)} className="p-1.5 border-2 border-brand-ink bg-white hover:bg-brand-peach">
                      <Trash weight="bold" size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}



function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 border-2 border-brand-ink text-xs font-bold uppercase tracking-wide transition-all ${
        active ? "bg-brand-pink text-white shadow-brutalSm" : "bg-white hover:bg-brand-yellow"
      }`}
    >
      {children}
    </button>
  );
}

function ImportCsvModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = () => {
    const rows = [
      ["codigo", "descricao", "preco", "departamento", "contratante"],
      ["001", "Blusa cropped rosa", "R$ 79,90", "Blusas", ""],
      ["002", "Calca jeans skinny", "129,90", "Calcas", "Contratante X"],
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "modelo_produtos.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Selecione um arquivo CSV");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/products/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Importação concluída: ${data.created} criados, ${data.updated} atualizados`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao importar");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-brand-ink/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div data-testid="import-modal" className="card-brutal w-full max-w-2xl bg-white p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between mb-4">
          <div>
            <p className="label-brutal">Importação em lote</p>
            <h2 className="font-heading text-2xl font-black">Importar produtos via CSV</h2>
            <p className="text-brand-ink2 text-sm mt-1">
              Colunas suportadas: <strong>codigo, descricao, preco, departamento, contratante</strong>. Delimitador `,` ou `;`. Produtos com o mesmo <strong>código</strong> serão atualizados.
            </p>
          </div>
          <button onClick={onClose} className="p-2 border-2 border-brand-ink"><X weight="bold" size={16} /></button>
        </div>

        <div className="card-brutal p-4 bg-brand-cream mb-4">
          <button data-testid="download-template" type="button" onClick={downloadTemplate} className="btn-white h-[44px] text-sm">
            <DownloadSimple weight="bold" size={16} /> Baixar modelo (.csv)
          </button>
          <p className="text-xs text-brand-ink2 mt-2">
            Departamentos e contratantes informados que ainda não existirem serão criados automaticamente.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="label-brutal">Arquivo CSV</label>
            <input
              data-testid="import-file"
              type="file"
              accept=".csv,text/csv"
              className="input-brutal file:mr-3 file:py-1 file:px-3 file:border-2 file:border-brand-ink file:bg-brand-yellow file:font-bold"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
            />
          </div>
          <div className="flex gap-2">
            <button data-testid="import-submit" type="submit" disabled={busy || !file} className="btn-pink h-[52px]">
              <UploadSimple weight="bold" size={16} /> {busy ? "Importando..." : "Importar"}
            </button>
            <button type="button" onClick={onClose} className="btn-white h-[52px]">Cancelar</button>
          </div>
        </form>

        {result && (
          <div data-testid="import-result" className="card-brutal p-4 bg-white mt-4 space-y-2">
            <div className="flex flex-wrap gap-2">
              <span className="badge-brutal !bg-brand-success !text-white"><strong>{result.created}</strong> criados</span>
              <span className="badge-brutal !bg-brand-yellow"><strong>{result.updated}</strong> atualizados</span>
              {result.total_errors > 0 && (
                <span className="badge-brutal !bg-brand-peach"><strong>{result.total_errors}</strong> erros</span>
              )}
            </div>
            {result.errors?.length > 0 && (
              <div className="text-xs bg-brand-cream border-2 border-brand-ink p-2 max-h-32 overflow-y-auto">
                <p className="font-bold mb-1">Erros:</p>
                {result.errors.slice(0, 20).map((e, i) => (
                  <div key={`${e.line}-${i}`}>Linha {e.line}: {e.error}</div>
                ))}
                {result.errors.length > 20 && <div>... e mais {result.errors.length - 20}.</div>}
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onImported} className="btn-pink h-[44px] text-sm">Concluir e recarregar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PriceCell({ product, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  const openEditor = (e) => {
    e?.stopPropagation();
    setValue(String(product.price ?? 0).replace(".", ","));
    setEditing(true);
  };

  const save = async () => {
    if (saving) return;
    const trimmed = String(value).trim();
    // Reject empty / non-numeric input (parseMoney returns 0 for these, which would silently zero the price)
    if (!trimmed || !/\d/.test(trimmed) || !/^-?\s*[\d.,]+$/.test(trimmed)) {
      return toast.error("Informe um valor válido");
    }
    const parsed = parseMoney(trimmed);
    if (isNaN(parsed) || parsed < 0) return toast.error("Informe um valor válido");
    if (parsed === product.price) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.put(`/products/${product.id}`, {
        code: product.code,
        description: product.description,
        department_id: product.department_id || null,
        contractor_id: product.contractor_id || null,
        price: parsed,
      });
      toast.success(`Preço atualizado para ${brl(parsed)}`);
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao atualizar preço");
    } finally { setSaving(false); }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); save(); }
    if (e.key === "Escape") setEditing(false);
  };

  if (editing) {
    return (
      <div className="inline-flex items-center gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
        <span className="text-brand-ink2 text-xs">R$</span>
        <input
          data-testid={`price-edit-input-${product.id}`}
          autoFocus
          inputMode="decimal"
          className="input-brutal !h-9 !py-1 w-24 text-right text-sm"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <button
          type="button"
          disabled={saving}
          data-testid={`price-edit-save-${product.id}`}
          onClick={save}
          title="Salvar preço"
          className="p-1.5 border-2 border-brand-ink bg-brand-mint hover:bg-brand-yellow disabled:opacity-60"
        >
          <FloppyDisk weight="bold" size={12} />
        </button>
        <button
          type="button"
          data-testid={`price-edit-cancel-${product.id}`}
          onClick={() => setEditing(false)}
          title="Cancelar"
          className="p-1.5 border-2 border-brand-ink bg-white hover:bg-brand-peach"
        >
          <X weight="bold" size={12} />
        </button>
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-1 justify-end">
      <span data-testid={`price-value-${product.id}`}>{brl(product.price)}</span>
      <button
        type="button"
        data-testid={`price-edit-btn-${product.id}`}
        onClick={openEditor}
        title="Editar preço"
        className="p-1 border-2 border-brand-ink bg-white hover:bg-brand-yellow"
      >
        <PencilSimple weight="bold" size={10} />
      </button>
    </div>
  );
}


function StockCell({ product, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(product.stock_balance ?? 0));
  const [saving, setSaving] = useState(false);
  const balance = product.stock_balance ?? 0;
  const badgeBg = balance > 0 ? "!bg-brand-mint" : balance < 0 ? "!bg-brand-peach" : "!bg-white";

  const openEditor = () => {
    setValue(String(balance));
    setEditing(true);
  };

  const save = async () => {
    if (saving) return;
    const target = parseInt(value, 10);
    if (isNaN(target)) return toast.error("Informe um número inteiro");
    const diff = target - balance;
    if (diff === 0) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.post(`/products/${product.id}/stock`, {
        kind: diff > 0 ? "in" : "out",
        quantity: Math.abs(diff),
        note: "Ajuste de inventário",
      });
      toast.success(`Saldo ajustado para ${target}`);
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao ajustar saldo");
    } finally { setSaving(false); }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); save(); }
    if (e.key === "Escape") { setEditing(false); }
  };

  if (editing) {
    return (
      <div className="inline-flex items-center gap-1 justify-end">
        <input
          data-testid={`stock-adjust-input-${product.id}`}
          autoFocus
          type="number"
          inputMode="numeric"
          className="input-brutal !h-9 !py-1 w-16 text-right text-sm"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <button
          type="button"
          disabled={saving}
          data-testid={`stock-adjust-save-${product.id}`}
          onClick={save}
          title="Confirmar"
          className="p-1.5 border-2 border-brand-ink bg-brand-mint hover:bg-brand-yellow disabled:opacity-60"
        >
          <FloppyDisk weight="bold" size={12} />
        </button>
        <button
          type="button"
          data-testid={`stock-adjust-cancel-${product.id}`}
          onClick={() => setEditing(false)}
          title="Cancelar"
          className="p-1.5 border-2 border-brand-ink bg-white hover:bg-brand-peach"
        >
          <X weight="bold" size={12} />
        </button>
      </div>
    );
  }

  return (
    <div className="inline-flex flex-col items-end gap-0.5">
      <div className="inline-flex items-center gap-1">
        <span data-testid={`stock-balance-${product.id}`} className={`badge-brutal ${badgeBg}`}>
          {balance}
        </span>
        <button
          type="button"
          data-testid={`stock-adjust-btn-${product.id}`}
          onClick={openEditor}
          title="Ajustar saldo"
          className="p-1 border-2 border-brand-ink bg-white hover:bg-brand-yellow"
        >
          <PencilSimple weight="bold" size={10} />
        </button>
      </div>
      <div className="text-[10px] text-brand-ink2">+{product.stock_in} / -{product.stock_out}</div>
    </div>
  );
}
